// src/pages/Products.jsx
import { motion } from 'framer-motion';
import { MainLayout } from 'components/Layout';
import { useEffect } from 'react';

export const Products = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <MainLayout
      title="Products — Northup"
      description="Explore our flagship technology products designed for the modern African digital ecosystem."
    >
      <div className="mx-auto max-w-6xl pb-20">
        {/* HERO */}
        <header className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
            className="space-y-6 text-center"
          >
            <span className="inline-block rounded-full border border-slate-700/70 bg-slate-900/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.21em] text-[#9D5666] backdrop-blur">
              Our Products
            </span>

            <h1 className="text-4xl font-semibold tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              Beautifully crafted experiences for Africa's digital future
            </h1>

            <p className="mx-auto max-w-2xl text-sm text-slate-400 sm:text-base">
              We design technologies made for scale, reliability, and the unique realities of the
              African market. Our first major product—JustKnow—creates a seamless relationship
              between users, service providers, and delivery networks.
            </p>
          </motion.div>
        </header>

        {/* PRODUCT SECTION: JUSTKNOW */}
        <section className="mb-28">
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <h2 className="text-3xl font-semibold text-slate-50 sm:text-4xl">
              JustKnow — Social Commerce & Connected Mobility
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-slate-400 sm:text-base">
              A next-generation social platform that lets users connect with businesses, place
              orders, request rides, and enjoy personalized transportation experiences.
            </p>
          </motion.div>

          <div className="grid gap-10 lg:grid-cols-2">
            {/* Left: App Preview */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative flex items-center justify-center"
            >
              <div className="relative w-[85%] rounded-3xl border border-slate-800/60 bg-slate-900/40 p-4 backdrop-blur-xl shadow-[0_22px_60px_rgba(15,23,42,0.9)]">
                <div
                  aria-hidden
                  className="absolute -inset-px rounded-3xl bg-gradient-to-tr from-cyan-500/20 via-purple-500/20 to-fuchsia-500/20 mix-blend-overlay"
                />

                {/* Mockup UI */}
                <div className="relative space-y-4">
                  <div className="h-3 w-24 rounded-full bg-slate-700/70" />
                  <div className="rounded-xl border border-slate-700/70 bg-slate-800/50 p-4">
                    <p className="text-sm font-medium text-slate-300">Nearby Businesses</p>
                    <div className="mt-2 grid grid-cols-3 gap-3">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-20 rounded-md bg-gradient-to-br from-slate-800 to-slate-900"
                        />
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-700/70 bg-slate-800/50 p-4">
                    <p className="text-sm font-medium text-slate-300">Place an Order</p>
                    <div className="mt-2 h-12 rounded-md bg-slate-900/60" />
                  </div>

                  <div className="rounded-xl border border-slate-700/70 bg-slate-800/50 p-4">
                    <p className="text-sm font-medium text-slate-300">Request a Ride</p>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="h-10 w-1/2 rounded-md bg-slate-900/60" />
                      <div className="h-10 w-1/3 rounded-md bg-cyan-500/20" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Details */}
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="flex flex-col justify-center space-y-8"
            >
              <ProductFeature
                title="Connect with businesses"
                description="Discover businesses around you—from restaurants to retail shops— and interact with them instantly."
              />

              <ProductFeature
                title="Order anything, delivered fast"
                description="Place orders directly in-app and get doorstep delivery fulfilled by JustKnow Riders."
              />

              <ProductFeature
                title="Personalized ride requests"
                description="Request trips, choose your preferred route, vehicle type, and even negotiate or set your desired fare."
              />

              <ProductFeature
                title="Unified social experience"
                description="A social timeline where users can engage with businesses, see trending services, and experience community-driven commerce."
              />
            </motion.div>
          </div>
        </section>

        {/* Secondary feature grid */}
        <section className="mb-28">
          <motion.h3
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-8 text-center text-2xl font-semibold text-slate-50"
          >
            What makes JustKnow different?
          </motion.h3>

          <div className="grid gap-5 md:grid-cols-3">
            <ValueCard
              title="Built for African markets"
              description="Connectivity-friendly, cost-efficient, and designed for the realities of African urban and rural mobility."
            />
            <ValueCard
              title="Commerce x Mobility"
              description="The first app blending ride-hailing, deliveries, and business discovery into one ecosystem."
            />
            <ValueCard
              title="Social + Utility"
              description="A social experience with real-world utility—more than likes, users can order, move, and interact."
            />
          </div>
        </section>

        {/* CTA */}
        <CTA />
      </div>
    </MainLayout>
  );
};

/* ---------------------------------------------------- *
 * Reusable Components
 * ---------------------------------------------------- */

const ProductFeature = ({ title, description }) => (
  <div className="space-y-2">
    <h4 className="text-lg font-semibold text-slate-100">{title}</h4>
    <p classname="text-sm text-slate-400">{description}</p>
  </div>
);

const ValueCard = ({ title, description }) => (
  <motion.div
    whileHover={{
      y: -4,
      scale: 1.02,
      boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
    }}
    transition={{ type: 'spring', stiffness: 260, damping: 20 }}
    className="rounded-2xl border border-slate-800/70 bg-slate-900/60 p-5 backdrop-blur"
  >
    <h4 className="mb-2 text-base font-semibold text-slate-100">{title}</h4>
    <p className="text-sm text-slate-400">{description}</p>
  </motion.div>
);

const CTA = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="mx-auto max-w-3xl rounded-3xl border border-slate-800 bg-slate-900/60 p-10 text-center shadow-[0_0_40px_rgba(56,189,248,0.12)] backdrop-blur-xl"
  >
    <h3 className="text-2xl font-semibold text-slate-50">
      Be one of the first to experience JustKnow
    </h3>
    <p className="mt-3 text-sm text-slate-400">
      We’re opening early access to selected users and businesses.
    </p>

    <button className="mt-6 rounded-full bg-gradient-to-r from-[#9D5666] via-[#C4C5C7] to-[#9D5666] px-6 py-3 text-sm font-semibold text-slate-950 shadow-lg transition hover:brightness-110">
      Request Early Access
    </button>
  </motion.div>
);
